// /app/buyers/trash — soft-deleted Cash Buyers / Power-Team members.
//
// Mirrors /app/leads/trash. Reuses the same 30-day grace window. Restore
// brings the buyer back into the matching engine; Purge skips the wait.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ArrowLeft, RotateCcw, AlertTriangle, Loader2, User as UserIcon, Phone, Mail } from "lucide-react";

const PURGE_AFTER_DAYS = 30;
const daysSince = (iso) => iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : 0;

export default function BuyersTrash() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/trash");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load trash");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (m) => {
    setBusy((b) => ({ ...b, [m.id]: "restoring" }));
    setRows((r) => r.filter((x) => x.id !== m.id));
    try {
      await api.post(`/team/${m.id}/restore`);
      toast.success(`Restored "${m.name || "buyer"}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
      load();
    } finally { setBusy((b) => { const c = { ...b }; delete c[m.id]; return c; }); }
  };

  const purge = async (m) => {
    setBusy((b) => ({ ...b, [m.id]: "purging" }));
    try {
      await api.delete(`/team/${m.id}/purge`);
      setRows((r) => r.filter((x) => x.id !== m.id));
      toast.success("Permanently deleted");
      setPurging(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setBusy((b) => { const c = { ...b }; delete c[m.id]; return c; }); }
  };

  return (
    <div className="space-y-6" data-testid="buyers-trash-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/app/buyers" className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-1)] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back to Buyers
          </Link>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1 flex items-center gap-2">
            <Trash2 className="size-7 text-rose-500" /> Buyer Trash
          </h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Deleted buyers and team members stay here for <strong>{PURGE_AFTER_DAYS} days</strong> — their
            buy-box criteria, deal history, and notes are preserved until then.
          </p>
        </div>
        <div className="rounded-xl border border-[var(--line)] bg-[var(--panel)] px-4 py-2 text-xs text-[var(--ink-2)]" data-testid="buyers-trash-count">
          <strong className="text-[var(--ink-0)] text-base">{rows.length}</strong> in trash
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]"><Loader2 className="size-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--panel)] p-12 text-center" data-testid="buyers-trash-empty">
          <Trash2 className="size-12 text-[var(--ink-3)] mx-auto mb-3" />
          <h3 className="font-semibold text-[var(--ink-0)] mb-1">Trash is empty</h3>
          <p className="text-sm text-[var(--ink-2)]">Deleted buyers and team members will show up here for 30 days.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[var(--line)] bg-[var(--panel)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--bg)] text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-bold">
              <tr>
                <th className="text-left px-4 py-3">Name / Role</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Deleted</th>
                <th className="text-left px-4 py-3">Purges in</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const age = daysSince(m.deleted_at);
                const remaining = Math.max(0, PURGE_AFTER_DAYS - age);
                const urgent = remaining <= 3;
                return (
                  <tr key={m.id} className="border-t border-[var(--line)] hover:bg-[var(--bg)]/40 transition" data-testid={`buyers-trash-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sm text-[var(--ink-0)] flex items-center gap-1.5">
                        <UserIcon size={12} className="text-[var(--ink-3)]" />
                        {m.name || "—"}
                      </div>
                      <div className="text-xs text-[var(--ink-3)] ml-[18px]">{m.role || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)] space-y-0.5">
                      {m.email && <div className="flex items-center gap-1"><Mail size={10} /> {m.email}</div>}
                      {m.phone && <div className="flex items-center gap-1"><Phone size={10} /> {m.phone}</div>}
                      {!m.email && !m.phone && <span className="text-[var(--ink-3)]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--ink-2)]">{age === 0 ? "today" : `${age}d ago`}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold " +
                        (urgent ? "bg-rose-100 text-rose-700 border border-rose-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200")}>
                        {urgent && <AlertTriangle size={11} />}{remaining}d
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => restore(m)} disabled={!!busy[m.id]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                        data-testid={`buyers-trash-restore-${m.id}`}>
                        {busy[m.id] === "restoring" ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Restore
                      </button>
                      <button onClick={() => setPurging(m)} disabled={!!busy[m.id]}
                        className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                        data-testid={`buyers-trash-purge-${m.id}`}>
                        <Trash2 size={11} /> Purge now
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {purging && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={() => !busy[purging.id] && setPurging(null)} data-testid="buyers-trash-purge-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0"><AlertTriangle size={20} /></div>
              <div>
                <h3 className="font-display font-bold text-lg text-[var(--ink)] leading-tight">Permanently delete this buyer?</h3>
                <p className="text-sm text-[var(--ink-2)] mt-1">
                  <strong>{purging.name || "This contact"}</strong> + their buy-box criteria, deal history, and notes
                  will be hard-deleted. Skipping the 30-day grace period — this <strong>cannot</strong> be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPurging(null)} disabled={!!busy[purging.id]}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-[var(--ink-1)] hover:bg-[var(--bg)] disabled:opacity-50"
                      data-testid="buyers-trash-purge-cancel">Cancel</button>
              <button onClick={() => purge(purging)} disabled={!!busy[purging.id]}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50"
                      data-testid="buyers-trash-purge-confirm">
                {busy[purging.id] === "purging" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Yes, purge permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
